// Health watch: spots worrying patterns in what's been logged and says, in
// plain words, whether to call the vet now, today, or just keep an eye on
// it. Pure; used by Today, the voice assistant and the phone alerts.
//
// Conservative on purpose — a puppy dehydrates and drops his blood sugar
// fast, and before he's fully vaccinated vomiting + diarrhoea can be
// parvovirus. It never says "it's fine": at worst it's quiet.
// Thresholds follow docs/RESEARCH.md §11 and the red flags in
// src/content/emergency.ts.

import { HOUR, MINUTE, formatTime, today } from './time.ts';
import type { CareItem, LogEvent, Millis, Profile, Symptom } from './types.ts';

export type HealthLevel = 'vet-now' | 'vet-today' | 'watch';

export interface HealthFlag {
  /** Stable per episode (includes the first event's id), so an alert is sent once. */
  key: string;
  level: HealthLevel;
  title: string;
  /** What was logged, e.g. "3 runny poos since 07:10". */
  evidence: string;
  /** What to do. */
  advice: string;
}

const LEVEL_ORDER: Record<HealthLevel, number> = { 'vet-now': 0, 'vet-today': 1, watch: 2 };

export const LEVEL_LABEL: Record<HealthLevel, string> = {
  'vet-now': 'Call the vet now',
  'vet-today': 'Call the vet today',
  watch: 'Keep an eye on it',
};

/** No puppy is fully protected this young: the earliest course ends with a 2nd jab at 10 weeks and a week to take effect. */
export const MIN_PROTECTED_WEEKS = 11;

/**
 * Fully protected once the vet's "go out" date is ticked off, or it has
 * passed with the 2nd vaccination done — and never before 11 weeks old,
 * whatever the care plan says (a date ticked early mustn't soften the
 * health watch for a 9-week-old).
 */
export function fullyVaccinated(care: CareItem[], now: Millis, profile?: Pick<Profile, 'dob'>): boolean {
  if (profile?.dob && now - new Date(`${profile.dob}T12:00`).getTime() < MIN_PROTECTED_WEEKS * 7 * 24 * HOUR) return false;
  const out = care.find((c) => c.template === 'out-and-about');
  if (out?.done) return true;
  const vax2 = care.find((c) => c.template === 'vax-2');
  return !!vax2?.done && !!out?.due && out.due <= today(now);
}

interface Episode {
  /** Matching events inside the window, oldest first. */
  events: LogEvent[];
  first: LogEvent;
  /**
   * Where this bout began: walking back from the window through matching
   * events no more than `ms` apart. Doesn't move as the window slides, so an
   * ongoing bout keeps one key (one alert, one dismissal).
   */
  start: LogEvent;
}

function within(events: LogEvent[], now: Millis, ms: number, pred: (e: LogEvent) => boolean): Episode | undefined {
  const all = events.filter((e) => e.at <= now && pred(e)).sort((a, b) => a.at - b.at);
  const hits = all.filter((e) => now - e.at <= ms);
  if (!hits.length) return undefined;
  let i = all.indexOf(hits[0]);
  while (i > 0 && all[i].at - all[i - 1].at <= ms) i--;
  return { events: hits, first: hits[0], start: all[i] };
}

const symptom =
  (...s: Symptom[]) =>
  (e: LogEvent) =>
    e.type === 'health' && s.includes(e.data.symptom);

const runnyPoo = (e: LogEvent) => (e.type === 'poo' && e.data.quality === 'runny') || symptom('diarrhoea')(e);
const loosePoo = (e: LogEvent) => (e.type === 'poo' && (e.data.quality === 'runny' || e.data.quality === 'soft')) || symptom('diarrhoea')(e);
const vomit = symptom('vomit');
const refusedMeal = (e: LogEvent) => (e.type === 'meal' && e.data.appetite === 'none') || symptom('not-eating')(e);

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export interface HealthInput {
  events: LogEvent[];
  care: CareItem[];
  profile?: Profile;
  now: Millis;
}

/** Blood (or black, tarry poo) mentioned in a note on a poo, a vomit or a health log. */
const BLOOD = /\b(blood|bloody|bleeding|tarry|tar-like|coffee.?grounds?)\b|\bblack (poo|stool|and tarry)\b/i;
const bloody = (e: LogEvent) => (e.type === 'poo' || e.type === 'health') && !!e.note && BLOOD.test(e.note);

export function healthWatch({ events, care, profile, now }: HealthInput): HealthFlag[] {
  const flags: HealthFlag[] = [];
  const ageWeeks = profile?.dob ? (now - new Date(`${profile.dob}T12:00`).getTime()) / (7 * 24 * HOUR) : undefined;
  const unprotected = !fullyVaccinated(care, now, profile);
  const parvo = unprotected ? ` He isn’t fully vaccinated yet, so phone ahead — the practice may want to keep him away from other puppies (parvovirus).` : '';

  const runny24 = within(events, now, 24 * HOUR, runnyPoo);
  const vomit24 = within(events, now, 24 * HOUR, vomit);
  const vomit12 = within(events, now, 12 * HOUR, vomit);
  const refused = within(events, now, 24 * HOUR, refusedMeal);
  const dull24 = within(events, now, 24 * HOUR, symptom('lethargic'));

  // --- Blood in his poo or sick (from a note) ------------------------------------------
  const blood = within(events, now, 24 * HOUR, bloody);
  if (blood)
    flags.push({
      key: `blood:${blood.start.id}`,
      level: 'vet-now',
      title: 'Blood in his poo or sick',
      evidence: `Noted at ${formatTime(blood.first.at)}: “${blood.first.note}”.`,
      advice: `Call the vet now — blood in a puppy’s poo or vomit always needs a vet.${parvo}`,
    });

  // --- Vomiting and diarrhoea together ------------------------------------------------
  if (runny24 && vomit24) {
    const first = runny24.first.at < vomit24.first.at ? runny24.first : vomit24.first;
    const start = runny24.start.at < vomit24.start.at ? runny24.start : vomit24.start;
    flags.push({
      key: `vd:${start.id}`,
      level: 'vet-now',
      title: 'Vomiting and diarrhoea',
      evidence: `${count(vomit24.events.length, 'vomit')} and ${count(runny24.events.length, 'runny poo')} since ${formatTime(first.at)}.`,
      advice: `Puppies dehydrate quickly — call the vet now.${parvo}`,
    });
  } else {
    // --- Diarrhoea ------------------------------------------------------------------------
    if (runny24 && runny24.events.length >= 2)
      flags.push({
        key: `diarrhoea:${runny24.start.id}`,
        level: unprotected || runny24.events.length >= 4 ? 'vet-now' : 'vet-today',
        title: 'Diarrhoea',
        evidence: `${count(runny24.events.length, 'runny poo')} since ${formatTime(runny24.first.at)}.`,
        advice: `Keep water down and call the vet${unprotected || runny24.events.length >= 4 ? ' now' : ' today'} — sooner if there’s blood, he’s vomiting or he goes quiet.${parvo}`,
      });
    else {
      const loose48 = within(events, now, 48 * HOUR, loosePoo);
      if (loose48 && loose48.events.length >= 3)
        flags.push({
          key: `loose:${loose48.start.id}`,
          level: 'watch',
          title: 'Soft poos',
          evidence: `${count(loose48.events.length, 'soft or runny poo')} in the last two days.`,
          advice: 'Common after a food change, too many treats or stress. Keep meals and treats plain and regular. Call the vet if it turns runny, has blood in it, or he seems unwell.',
        });
    }

    // --- Vomiting ---------------------------------------------------------------------------
    if (vomit12 && vomit12.events.length >= 3)
      flags.push({
        key: `vomit:${vomit12.start.id}`,
        level: 'vet-now',
        title: 'Repeated vomiting',
        evidence: `${count(vomit12.events.length, 'vomit')} since ${formatTime(vomit12.first.at)}.`,
        advice: `Call the vet now. Offer small sips of water, not food, until you’ve spoken to them.${parvo}`,
      });
    else if (vomit24 && vomit24.events.length >= 2)
      flags.push({
        key: `vomit:${vomit24.start.id}`,
        // Not yet fully vaccinated, being sick more than once is a call now (parvovirus often starts this way).
        level: unprotected ? 'vet-now' : 'vet-today',
        title: 'Vomiting',
        evidence: `${count(vomit24.events.length, 'vomit')} since ${formatTime(vomit24.first.at)}.`,
        advice: unprotected
          ? `Call the vet now — he’s not fully vaccinated, and sickness can be the first sign of parvovirus. Offer small sips of water, not food, until you’ve spoken to them.${parvo}`
          : `Call the vet today — now if it keeps happening, there’s blood, his belly looks swollen or he’s retching with nothing coming up.`,
      });
  }

  // --- Off his food ------------------------------------------------------------------------
  // Two refused meals in a row (or logged "not eating") — young puppies can't go long without.
  const meals = events.filter((e) => e.type === 'meal' && e.at <= now && now - e.at <= 24 * HOUR).sort((a, b) => b.at - a.at);
  const lastTwoRefused = meals.length >= 2 && meals.slice(0, 2).every(refusedMeal);
  // The first of the current run of refused meals (stays put as more are refused).
  const allMeals = events.filter((e) => e.type === 'meal' && e.at <= now).sort((a, b) => b.at - a.at);
  let run = 0;
  while (run < allMeals.length && refusedMeal(allMeals[run])) run++;
  const runStart = allMeals[Math.max(0, run - 1)];
  const loggedNotEating = within(events, now, 24 * HOUR, symptom('not-eating'));
  if (lastTwoRefused || loggedNotEating) {
    const firstMiss = lastTwoRefused ? meals[1] : loggedNotEating!.first;
    const unwell = !!(dull24 || vomit24 || runny24);
    flags.push({
      key: `appetite:${(lastTwoRefused ? runStart : loggedNotEating!.start).id}`,
      level: unwell ? 'vet-now' : 'vet-today',
      title: 'Off his food',
      evidence: lastTwoRefused ? `Didn’t eat his last two meals (from ${formatTime(firstMiss.at)}).` : `Logged “not eating” at ${formatTime(firstMiss.at)}.`,
      advice: unwell
        ? 'Not eating and unwell as well — call the vet now. Young puppies can drop their blood sugar quickly.'
        : 'Call the vet today. Check his gums are pink and wet, and that he’s drinking. If he’s wobbly, very sleepy or twitchy, call now — young puppies can drop their blood sugar quickly.',
    });
  }

  // --- Lethargy alongside anything else ------------------------------------------------------
  if (dull24 && !flags.some((f) => f.level === 'vet-now') && (vomit24 || runny24 || refused))
    flags.push({
      key: `dull:${dull24.start.id}`,
      level: 'vet-now',
      title: 'Quiet and unwell',
      evidence: `Logged lethargic at ${formatTime(dull24.first.at)}, with other symptoms today.`,
      advice: 'A flat, quiet puppy who is also being sick, has diarrhoea or won’t eat needs a vet now.',
    });
  // --- Lethargy on its own, in a young puppy ------------------------------------------------
  else if (dull24 && !flags.some((f) => f.level === 'vet-now') && ageWeeks !== undefined && ageWeeks < 16)
    flags.push({
      key: `dull:${dull24.start.id}`,
      level: unprotected ? 'vet-now' : 'vet-today',
      title: 'Quiet and flat',
      evidence: `Logged lethargic at ${formatTime(dull24.first.at)}.`,
      advice: `A young puppy who’s unusually flat needs a vet check today — call now if he’s floppy, cold, trembling, won’t wake properly, or his gums are pale or tacky.${parvo}`,
    });

  // --- Ate something --------------------------------------------------------------------------
  const ate = within(events, now, 6 * HOUR, symptom('ate-something'));
  if (ate)
    flags.push({
      key: `ate:${ate.start.id}`,
      level: 'vet-now',
      title: 'Ate something he shouldn’t',
      evidence: `Logged at ${formatTime(ate.first.at)}${ate.first.note ? `: “${ate.first.note}”` : ''}.`,
      advice: 'If it could be poisonous (chocolate, grapes/raisins, xylitol, medicines, a slug pellet…) or an object, call the vet now — even if he seems fine. Animal PoisonLine: 01202 509000. Don’t make him sick unless the vet says so.',
    });

  // --- Anything logged as severe ----------------------------------------------------------------
  const severe = within(events, now, 24 * HOUR, (e) => e.type === 'health' && e.data.severity === 3);
  if (severe && !flags.some((f) => f.level === 'vet-now'))
    flags.push({
      key: `severe:${severe.start.id}`,
      level: 'vet-today',
      title: 'Logged as severe',
      evidence: `Marked severe at ${formatTime(severe.first.at)}.`,
      advice: 'If you were worried enough to mark it severe, call the vet — they’d always rather hear from you.',
    });

  // --- Coughing ---------------------------------------------------------------------------------
  const cough = within(events, now, 48 * HOUR, symptom('coughing'));
  if (cough && cough.events.length >= 2)
    flags.push({
      key: `cough:${cough.start.id}`,
      level: 'vet-today',
      title: 'Coughing',
      evidence: `${count(cough.events.length, 'cough', 'coughing episodes')} logged in two days.`,
      advice: 'Could be kennel cough. Call the vet today and keep him away from other dogs. Call now if he’s struggling to breathe, off his food or very quiet.',
    });

  // --- Ears and itching (cockers are prone to ear infections) --------------------------------------
  const ears = within(events, now, 7 * 24 * HOUR, symptom('ear-shaking', 'itching'));
  if (ears && ears.events.length >= 3)
    flags.push({
      key: `ears:${ears.start.id}`,
      level: 'watch',
      title: 'Ears or itching',
      evidence: `${count(ears.events.length, 'time')} this week.`,
      advice: 'Cocker ears trap warmth and moisture, so infections are common. Look inside: redness, a smell or brown wax means book a vet check. Dry his ears after walks and baths.',
    });

  // --- Limping ------------------------------------------------------------------------------------
  const limp = within(events, now, 72 * HOUR, symptom('limping'));
  if (limp && limp.events.length >= 2)
    flags.push({
      key: `limp:${limp.start.id}`,
      level: 'vet-today',
      title: 'Limping',
      evidence: `${count(limp.events.length, 'time')} since ${new Date(limp.first.at).toLocaleDateString('en-GB', { weekday: 'long' })}.`,
      advice: 'A limp that comes back is worth a vet check — puppy joints are still growing. Rest him (no stairs or jumping) until then.',
    });

  // --- Not gaining weight ---------------------------------------------------------------------------
  const weighs = events.filter((e): e is LogEvent<'weight'> => e.type === 'weight' && e.at <= now).sort((a, b) => b.at - a.at);
  if (weighs.length >= 2 && now - weighs[0].at < 14 * 24 * HOUR) {
    const [latest, prev] = weighs;
    const days = (latest.at - prev.at) / (24 * HOUR);
    if (days >= 5 && latest.data.kg < prev.data.kg - 0.05)
      flags.push({
        key: `weight:${latest.id}`,
        level: 'vet-today',
        title: 'Lost weight',
        evidence: `${latest.data.kg.toFixed(2)} kg, down from ${prev.data.kg.toFixed(2)} kg ${Math.round(days)} days ago.`,
        advice: 'A growing puppy should gain every week. Re-weigh on the same scales to be sure, then ring the vet.',
      });
  }

  // --- No poo for a long while (only while logging is active) --------------------------------------
  const lastPoo = events.filter((e) => e.type === 'poo' && e.at <= now).reduce((m, e) => Math.max(m, e.at), 0);
  const recentWees = events.filter((e) => e.type === 'wee' && e.at <= now && now - e.at <= 8 * HOUR).length;
  if (lastPoo && now - lastPoo > 36 * HOUR && recentWees >= 2)
    flags.push({
      key: `nopoo:${Math.round(lastPoo / MINUTE)}`,
      level: now - lastPoo > 48 * HOUR ? 'vet-today' : 'watch',
      title: 'No poo logged for a while',
      evidence: `Last poo logged ${Math.round((now - lastPoo) / HOUR)} hours ago.`,
      advice:
        now - lastPoo > 48 * HOUR
          ? 'Two days without a poo — ring the vet, and sooner if he’s straining, off his food or being sick (he might have swallowed something).'
          : 'Maybe it just wasn’t logged. If not, watch for straining, a sore tummy or being off his food.',
    });

  return flags.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
}
